/**
 * Organization utility functions for parsing and working with organization metadata
 */

import type { OrganizationMetadata, OrganizationType } from "@repo/db";

export type { OrganizationMetadata, OrganizationType };

/**
 * Check if onboarding has been completed for a creator organization.
 * Only returns false when `onboardingCompleted` is explicitly `false` (set during createWithRole).
 * Orgs created before this feature (missing the flag) are treated as already onboarded.
 * Agency orgs always return true (they skip onboarding).
 */
export function isOnboardingCompleted(
  metadata: string | null | undefined,
): boolean {
  const parsed = parseOrganizationMetadata(metadata);
  if (parsed.organizationType === "agency") return true;
  return parsed.onboardingCompleted !== false;
}

/**
 * Check if an organization needs role selection (no organizationType set yet).
 * This happens when `ensureUserHasOrganization` auto-creates a bare org
 * during post-login onboarding — the user still needs to pick creator vs agency.
 */
export function needsRoleSelection(
  metadata: string | null | undefined,
): boolean {
  const parsed = parseOrganizationMetadata(metadata);
  return !parsed.organizationType;
}

/**
 * Parse organization type from metadata JSON string
 * @param metadata - JSON string containing organization metadata
 * @returns Organization type, defaults to "creator" if parsing fails or type is not set
 */
export function getOrganizationType(
  metadata: string | null | undefined,
): "agency" | "creator" {
  const parsed = parseOrganizationMetadata(metadata);
  return parsed.organizationType || "creator";
}

/**
 * Safely parse organization metadata from a JSON string.
 * Returns an empty object if metadata is null/undefined/invalid.
 */
export function parseOrganizationMetadata(
  metadata: string | null | undefined,
): OrganizationMetadata {
  if (!metadata) return {};

  try {
    const parsed: unknown = JSON.parse(metadata);
    // Only accept plain objects — reject arrays, primitives, null
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return {};
    }
    return parsed as OrganizationMetadata;
  } catch {
    return {};
  }
}

/**
 * Get the completed onboarding step number from metadata.
 * Returns 0 if no steps have been completed.
 * Only accepts integers in the valid range (0–5).
 */
export function getOnboardingStep(metadata: string | null | undefined): number {
  const parsed = parseOrganizationMetadata(metadata);
  const step = parsed.onboardingStep;
  if (
    typeof step === "number" &&
    Number.isInteger(step) &&
    step >= 0 &&
    step <= 5
  ) {
    return step;
  }
  return 0;
}
